// frontend/src/components/BadgeRemovalReviewDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  CardMedia,
  Divider,
  Tooltip
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { format, isPast, formatDistanceToNow } from 'date-fns';
import apiClient from '../api/client';
import { getBadgeReviewImages, dismissBadgeAuditItem } from '../api/scheduler';

const ImagePanel = ({ title, imageUrl, error, color }) => (
  <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
    <Typography variant="subtitle2" gutterBottom color={color || 'text.primary'}>
      {title}
    </Typography>
    <Box
      sx={{
        width: '100%',
        height: 300,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      {error ? (
        <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
          {error}
        </Typography>
      ) : imageUrl ? (
        <CardMedia
          component="img"
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            cursor: 'pointer'
          }}
          image={imageUrl}
          alt={title}
          onClick={() => window.open(imageUrl, '_blank')}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = '<span style="color: #999; padding: 16px;">Failed to load image</span>';
          }}
        />
      ) : (
        <CircularProgress size={24} />
      )}
    </Box>
  </Box>
);

const BadgeRemovalReviewDialog = ({
  open,
  onClose,
  auditItems,
  onApprove,
  onDismiss,
  onComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processedItems, setProcessedItems] = useState(new Set());

  // Combine missing and failed items into a single reviewable list
  const allItems = React.useMemo(() => {
    if (!auditItems) return [];
    const items = [];
    if (auditItems.submissions) {
      auditItems.submissions.forEach(s => items.push({
        ...s,
        type: 'missing',
        display_badges: s.badges
      }));
    }
    if (auditItems.failed_removals) {
      auditItems.failed_removals.forEach(s => items.push({
        ...s,
        type: 'failed',
        display_badges: s.original_badges || []
      }));
    }
    return items;
  }, [auditItems]);

  const currentItem = allItems[currentIndex];
  const remainingCount = allItems.length - processedItems.size;

  // Fetch comparison images when current item changes
  useEffect(() => {
    if (!open || !currentItem) return;

    const fetchImages = async () => {
      setLoading(true);
      setError(null);
      setReviewData(null);
      try {
        const data = await getBadgeReviewImages(currentItem.image_id);
        setReviewData(data);
      } catch (err) {
        setError('Failed to load comparison images');
        console.error('Error fetching review images:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [open, currentIndex, currentItem]);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setProcessedItems(new Set());
      setError(null);
    }
  }, [open]);

  const handleApprove = async () => {
    if (!currentItem) return;
    setActionLoading(true);
    try {
      if (onApprove) await onApprove(currentItem, reviewData);
      setProcessedItems(prev => new Set(prev).add(currentItem.submission_id));
      advanceToNext();
    } catch (err) {
      setError('Failed to approve badge removal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (!currentItem) return;
    setActionLoading(true);
    try {
      await dismissBadgeAuditItem(currentItem.submission_id);
      setProcessedItems(prev => new Set(prev).add(currentItem.submission_id));
      advanceToNext();
    } catch (err) {
      setError('Failed to dismiss item');
    } finally {
      setActionLoading(false);
    }
  };

  const advanceToNext = () => {
    // Find next unprocessed item
    for (let i = currentIndex + 1; i < allItems.length; i++) {
      if (!processedItems.has(allItems[i].submission_id)) {
        setCurrentIndex(i);
        return;
      }
    }
    // Wrap around
    for (let i = 0; i < currentIndex; i++) {
      if (!processedItems.has(allItems[i].submission_id)) {
        setCurrentIndex(i);
        return;
      }
    }
    // All done
    if (onComplete) onComplete();
    onClose();
  };

  const goToIndex = (direction) => {
    const next = currentIndex + direction;
    if (next >= 0 && next < allItems.length) {
      setCurrentIndex(next);
    }
  };

  if (!allItems.length) return null;

  const isProcessed = currentItem && processedItems.has(currentItem.submission_id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <LocalOfferIcon color="primary" />
            <Typography variant="h6">Badge Removal Review</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {currentIndex + 1} of {allItems.length}
              {processedItems.size > 0 && ` (${processedItems.size} processed)`}
            </Typography>
            <IconButton
              onClick={() => goToIndex(-1)}
              disabled={currentIndex === 0 || actionLoading}
              size="small"
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={() => goToIndex(1)}
              disabled={currentIndex === allItems.length - 1 || actionLoading}
              size="small"
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {currentItem && (
          <>
            {/* Item info header */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Box>
                  <Typography variant="h6">GTIN: {currentItem.image_gtin}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Submission #{currentItem.submission_id}
                    {' '}&mdash;{' '}
                    {currentItem.type === 'failed' ? 'Failed badge removal' : 'Missing badge removal'}
                  </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  {currentItem.display_badges?.map((badge, i) => (
                    <Chip
                      key={i}
                      label={badge}
                      size="small"
                      color="warning"
                      icon={<LocalOfferIcon />}
                    />
                  ))}
                  {currentItem.type === 'failed' && (
                    <Chip label="Failed" size="small" color="error" />
                  )}
                  {currentItem.type === 'missing' && (
                    <Chip label="Missing" size="small" color="warning" />
                  )}
                  {isProcessed && (
                    <Chip label="Processed" size="small" color="success" />
                  )}
                </Box>
              </Box>
              {/* Badge timeline */}
              {reviewData && (reviewData.badge_submitted_date || reviewData.badge_removal_due) && (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {reviewData.badge_submitted_date && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Badge Submitted:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {format(new Date(reviewData.badge_submitted_date), 'MMM d, yyyy h:mm a')}
                        {' '}({formatDistanceToNow(new Date(reviewData.badge_submitted_date), { addSuffix: true })})
                      </Typography>
                    </Box>
                  )}
                  {reviewData.badge_removal_due && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Removal Due:</Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={isPast(new Date(reviewData.badge_removal_due)) ? 'error' : 'text.primary'}
                      >
                        {format(new Date(reviewData.badge_removal_due), 'MMM d, yyyy')}
                        {isPast(new Date(reviewData.badge_removal_due))
                          ? ` (overdue by ${formatDistanceToNow(new Date(reviewData.badge_removal_due))})`
                          : ` (${formatDistanceToNow(new Date(reviewData.badge_removal_due), { addSuffix: true })})`
                        }
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {currentItem.error_message && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  Error: {currentItem.error_message}
                </Typography>
              )}
            </Box>

            {/* Asset health warnings */}
            {reviewData && reviewData.auto_remove_warnings?.length > 0 && (
              <Alert severity={reviewData.can_auto_remove ? "info" : "warning"} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {reviewData.can_auto_remove
                    ? "Note:"
                    : "Automatic badge removal will likely fail for this image:"
                  }
                </Typography>
                {reviewData.auto_remove_warnings.map((warning, i) => (
                  <Typography key={i} variant="body2" display="block">
                    {warning}
                  </Typography>
                ))}
                {!reviewData.can_auto_remove && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Consider dismissing this item if the badge has already been removed in Syndigo,
                    or manually edit the image in the Edit page and resubmit.
                  </Typography>
                )}
              </Alert>
            )}

            {/* Three-column image comparison */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading comparison images...</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ImagePanel
                  title="Our MRHI (Current)"
                  imageUrl={reviewData ? `${apiClient.defaults.baseURL}/images/${currentItem.image_id}/file?t=${Date.now()}` : null}
                  color="primary"
                />
                <ImagePanel
                  title="Syndigo (Live)"
                  imageUrl={reviewData?.syndigo_url}
                  error={reviewData?.syndigo_error}
                  color="success.main"
                />
                <ImagePanel
                  title="Kroger API (Source)"
                  imageUrl={reviewData?.kroger_url}
                  error={reviewData?.kroger_error}
                  color="info.main"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit">
          Close ({remainingCount} remaining)
        </Button>
        <Box display="flex" gap={1}>
          <Tooltip title="Mark as not needed - won't appear in future audits">
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleDismiss}
              disabled={actionLoading || isProcessed}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
            >
              Dismiss
            </Button>
          </Tooltip>
          <Tooltip title={
            reviewData && !reviewData.can_auto_remove
              ? "Automatic removal will likely fail — consider dismissing instead"
              : "Schedule badge removal for this image"
          }>
            <Button
              variant={reviewData && !reviewData.can_auto_remove ? "outlined" : "contained"}
              color={reviewData && !reviewData.can_auto_remove ? "warning" : "primary"}
              onClick={handleApprove}
              disabled={actionLoading || isProcessed}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <ScheduleIcon />}
            >
              {reviewData && !reviewData.can_auto_remove ? "Approve Anyway" : "Approve Removal"}
            </Button>
          </Tooltip>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeRemovalReviewDialog;
